import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

const faqs = [
  {
    question: 'Bracknell Forest has a GREEN spend rating — can I still claim for pothole damage?',
    answer: 'Yes. The DfT Spend scorecard is GREEN because projected 2025/26 capital spend (£6.929m) is more than double the DfT allocation (£3.293m). Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired — not on aggregate spending. The overall and condition ratings are both AMBER.',
  },
  {
    question: 'What if my pothole was on a residential or unclassified road?',
    answer: 'U-roads make up 323.2km — 66.9% of Bracknell Forest\'s 483.4km carriageway network. The council surveys U-roads bi-annually (north one year, south the next) using Coarse Visual Inspection, and publishes only RED-condition percentages — not full red/amber/green splits. At the 2022/23 survey, U-road RED condition hit 23%. If your incident fell between surveys or on a road type with thinner published data, the council\'s network-level scorecard may not reflect your street.',
  },
  {
    question: 'Does 0% preventative spend in 2020/21 mean the council did no maintenance?',
    answer: 'Not entirely. Bracknell Forest\'s transparency report states that preventative maintenance in 2020/21 was incorporated within Local Enterprise Partnership-funded schemes on Downshire Way and the A3095 — so no separate preventative line appeared in that year\'s table. Reactive maintenance still accounted for an estimated 45.7% of spend, and pothole patching continued. The figure is still evidence of how prevention was reported and prioritised in that financial year.',
  },
  {
    question: 'Does the recent rise to 30.4% preventative spend weaken my claim?',
    answer: 'Not automatically. If your damage occurred between 2020 and 2024, when preventative spend ranged from 0% to 16.5% and pothole repairs rose from 740 to 1,255, the council\'s own data describes a period of reactive-heavy maintenance on ageing New Town infrastructure. Later budget shifts do not erase inspection records, repair logs or photos from your incident date — those remain the core of a Section 58 claim.',
  },
  {
    question: 'Why did U-road RED condition spike to 23% in 2022/23?',
    answer: 'The council describes 2022/23 as "an anomaly in the data, possibly due to the COVID pandemic preventing maintenance works within residential areas during the preceding years." RED U-road condition fell back to 14% by 2024/25. That admission documents deferred maintenance coinciding with measurable deterioration — useful context if your claim involves a residential road from that period, but not a substitute for evidence about your specific defect.',
  },
  {
    question: 'The council says New Town infrastructure is approaching end of design life — does that help?',
    answer: 'It can provide context. Bracknell Forest states that subways, carriageways, footways and "almost every highway asset within the new town area footprint" built in the 1950s–60s are "aging together and approaching the end of their design life." That is documented knowledge of elevated maintenance need. Whether it strengthens your claim still depends on whether the council reasonably inspected and repaired the defect that damaged your vehicle.',
  },
  {
    question: 'How do I report a pothole to Bracknell Forest Council?',
    answer: 'Use the council\'s online form to report potholes and other road repairs. Prior reports of the same defect strengthen a claim by demonstrating the council had notice before your incident. Keep your reference number and any confirmation. Fixtyer searches for existing reports and attaches them to your claim pack.',
  },
];

export function BracknellForestCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Bracknell Forest Pothole Claims | 1,255 Repairs After 0% Prevention Year | Fixtyer"
        description="Bracknell Forest earns AMBER DfT ratings but programmed 0% preventative maintenance in 2020/21, surveys 323km of U-roads bi-annually, and filled 1,255 potholes in 2023/24. What that means for your Section 58 claim."
        canonical="/councils/bracknell-forest"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Bracknell Forest', url: '/councils/bracknell-forest' },
        ]}
      />
      <FAQSchema faqs={faqs} />
      <ServiceSchema
        councilName="Bracknell Forest Council"
        councilSlug="bracknell-forest"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/bracknell-forest"
        headline="Bracknell Forest Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Bracknell Forest</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            1,255 Pothole Repairs in 2023/24 After a 0% Prevention Year
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Bracknell Forest earns <strong className="text-amber-400">AMBER</strong> DfT scorecards for overall
            condition and best practice, with <strong className="text-emerald-400">GREEN</strong> spend — yet its
            own transparency report records <strong className="text-rose-400">0% programmed preventative maintenance
            in 2020/21</strong>, pothole repairs up from 740 to an estimated 1,100 by 2025/26, and{' '}
            <strong className="text-amber-400">323.2km of U-roads (67% of the network)</strong> surveyed only
            bi-annually. Much of the 1950s–60s New Town infrastructure is documented as approaching end of design
            life. Section 58 still turns on your specific defect.
          </p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">23%</div>
            <div className="text-white font-medium mt-2">U-road RED condition at 2022/23 survey</div>
            <div className="text-brand-300 text-sm mt-1">
              Up from 14% the year before — an anomaly the council links to COVID-deferred residential maintenance.
              U-roads are surveyed north/south on alternate years; only RED percentages are published.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* Network scale */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            483.4km of Roads — Mostly Residential New Town
          </h2>
          <p className="text-surface-600 mb-8">
            Network scale from Bracknell Forest&apos;s June 2025 transparency report — where pothole claims
            actually happen
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">483.4km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Total carriageway</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">323.2km</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Unclassified (U) roads</div>
              <div className="text-xs text-amber-700 mt-1">66.9% of the network</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">108.9km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">B and C roads</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">51.2km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">A roads</div>
              <div className="text-xs text-surface-500 mt-1">SCANNER surveyed annually</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full text-sm">
              <caption className="sr-only">Bracknell Forest highway asset breakdown</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Asset</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Scale</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Footways</td>
                  <td className="px-6 py-4 text-center font-medium">591.5km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Cycleways</td>
                  <td className="px-6 py-4 text-center font-medium">534.06km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Public rights of way</td>
                  <td className="px-6 py-4 text-center font-medium">80km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Structures</td>
                  <td className="px-6 py-4 text-center font-medium">193</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Highway gullies</td>
                  <td className="px-6 py-4 text-center font-medium">21,598</td>
                </tr>
                <tr>
                  <td className="px-6 py-4">Street lighting columns</td>
                  <td className="px-6 py-4 text-center font-medium">14,138</td>
                </tr>
              </tbody>
            </table>
          </div>

          <QuoteCard
            quote="Bracknell was constructed as a New Town from the late 1950s. While the characteristic planned dual carriageways, roundabouts, subways, cycle routes, and distributor roads of the Bracknell New Town could still be considered modern in relative terms, much of the infrastructure is now over 70 years old which presents unique maintenance challenges."
            source="Bracknell Forest Council Local Highways Maintenance Transparency Report (10 June 2025)"
          />
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the Published Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            SCANNER surveys on A, B and C roads annually; U-roads surveyed bi-annually by visual inspection
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <p className="text-amber-800 text-sm">
              <strong>U-road caveat:</strong> Bracknell Forest surveys U-roads bi-annually — north one year, south
              the next — using Coarse Visual Inspection. The transparency report publishes only RED-condition
              percentages for U-roads, not full red/amber/green splits. SCANNER data on classified roads is collected
              between spring and autumn at different times each year.
            </p>
          </div>

          {/* A roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A roads (51.2km) — annual SCANNER surveys</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020/21</td>
                    <td className="px-4 py-2 text-center">3.1%</td>
                    <td className="px-4 py-2 text-center">22.3%</td>
                    <td className="px-4 py-2 text-center">74.6%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022/23</td>
                    <td className="px-4 py-2 text-center">5.5%</td>
                    <td className="px-4 py-2 text-center">22.6%</td>
                    <td className="px-4 py-2 text-center">71.9%</td>
                  </tr>
                  <tr className="border-b border-surface-100 bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2023/24</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">7.2%</td>
                    <td className="px-4 py-2 text-center font-bold">27.1%</td>
                    <td className="px-4 py-2 text-center font-bold">65.8%</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-mono">2024/25</td>
                    <td className="px-4 py-2 text-center">6.4%</td>
                    <td className="px-4 py-2 text-center">23.6%</td>
                    <td className="px-4 py-2 text-center">70.0%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              The council states A-road condition worsened between 2021 and 2024 but has more recently started to
              improve. RED A-roads peaked at 7.2% in 2023/24 — more than double the 3.1% recorded in 2020/21.
            </p>
          </div>

          {/* B&C roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (108.9km) — stable on published data</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020/21</td>
                    <td className="px-4 py-2 text-center">2.4%</td>
                    <td className="px-4 py-2 text-center">22.9%</td>
                    <td className="px-4 py-2 text-center">74.7%</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-mono">2024/25</td>
                    <td className="px-4 py-2 text-center">3.3%</td>
                    <td className="px-4 py-2 text-center">19.4%</td>
                    <td className="px-4 py-2 text-center">77.3%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              The council describes B/C road condition as &quot;very consistent over the past 4 years&quot; — RED
              condition has remained between 2.4% and 3.7%.
            </p>
          </div>

          {/* U roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-amber-900 mb-4">Unclassified roads (323.2km) — RED % only, bi-annual survey</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020/21</td>
                    <td className="px-4 py-2 text-center">12%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021/22</td>
                    <td className="px-4 py-2 text-center">14%</td>
                  </tr>
                  <tr className="border-b border-amber-100 bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2022/23</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">23%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023/24</td>
                    <td className="px-4 py-2 text-center">19%</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-mono">2024/25</td>
                    <td className="px-4 py-2 text-center">14%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              At 23% RED in 2022/23, roughly 74km of the U-road network was in the worst published category —
              on roads surveyed only every other year by visual inspection.
            </p>
          </div>

          <QuoteCard
            quote="The condition of U roads is very consistent with the exception of 2022 to 2023 which represents an anomaly in the data, possibly due to the COVID pandemic preventing maintenance works within residential areas during the preceding years."
            source="Bracknell Forest Council Local Highways Maintenance Transparency Report (10 June 2025)"
          />
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">
            Following the Money
          </h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            GREEN spend — but preventative maintenance was minimal for years before a recent shift
          </p>

          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 mb-6 max-w-3xl">
            <p className="text-amber-200 text-sm">
              <strong className="text-amber-400">2020/21 caveat:</strong> The council notes that preventative
              maintenance in 2020/21 was incorporated within Local Enterprise Partnership-funded schemes on
              Downshire Way and the A3095 — hence 0% appears in the preventative column for that year. Surface
              dressing counts as preventative maintenance in this table.
            </p>
          </div>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">Bracknell Forest highway maintenance spending 2020-2026</caption>
              <thead>
                <tr className="bg-brand-800 border-b border-brand-700">
                  <th className="px-4 py-3 text-left font-semibold text-brand-300">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">DfT capital (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Capital spend (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Revenue spend (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Reactive</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-800">
                <tr className="bg-emerald-900/30">
                  <td className="px-4 py-3 font-medium">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center">3,293</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-400">6,929</td>
                  <td className="px-4 py-3 text-center">2,391</td>
                  <td className="px-4 py-3 text-center text-emerald-400">30.4%</td>
                  <td className="px-4 py-3 text-center">25.7%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2024/25</td>
                  <td className="px-4 py-3 text-center">2,392</td>
                  <td className="px-4 py-3 text-center">6,183</td>
                  <td className="px-4 py-3 text-center">2,383</td>
                  <td className="px-4 py-3 text-center">16.5%</td>
                  <td className="px-4 py-3 text-center">27.8%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2023/24</td>
                  <td className="px-4 py-3 text-center">2,392</td>
                  <td className="px-4 py-3 text-center">3,575</td>
                  <td className="px-4 py-3 text-center">2,661</td>
                  <td className="px-4 py-3 text-center">7.7%</td>
                  <td className="px-4 py-3 text-center">42.7%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2022/23</td>
                  <td className="px-4 py-3 text-center">2,124</td>
                  <td className="px-4 py-3 text-center">3,138</td>
                  <td className="px-4 py-3 text-center">2,680</td>
                  <td className="px-4 py-3 text-center">8.4%</td>
                  <td className="px-4 py-3 text-center">46.1%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2021/22</td>
                  <td className="px-4 py-3 text-center">2,124</td>
                  <td className="px-4 py-3 text-center">2,229</td>
                  <td className="px-4 py-3 text-center">2,384</td>
                  <td className="px-4 py-3 text-center">7.6%</td>
                  <td className="px-4 py-3 text-center">51.7%</td>
                </tr>
                <tr className="bg-amber-900/30">
                  <td className="px-4 py-3 font-medium">2020/21</td>
                  <td className="px-4 py-3 text-center">2,757</td>
                  <td className="px-4 py-3 text-center">2,254</td>
                  <td className="px-4 py-3 text-center">1,895</td>
                  <td className="px-4 py-3 text-center font-bold text-amber-400">0%</td>
                  <td className="px-4 py-3 text-center">45.7%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-emerald-500/20 rounded-xl p-6 border border-emerald-500/30">
              <h3 className="font-semibold text-white mb-3">Why spend is GREEN</h3>
              <p className="text-brand-200 text-sm">
                Capital spend rose from £2.3m in 2020/21 to a projected £6.9m in 2025/26, while the council
                supplements DfT grants with its own capital programme. The DfT rates that investment pattern
                above threshold — a credible funding story on paper.
              </p>
            </div>
            <div className="bg-amber-500/20 rounded-xl p-6 border border-amber-500/30">
              <h3 className="font-semibold text-white mb-3">Why claims still happen</h3>
              <p className="text-brand-200 text-sm">
                Between 2020/21 and 2023/24, estimated preventative share ranged from 0% to 16.5% while reactive
                spend ran at 42.7%–51.7%. Even with 30.4% preventative spend planned for 2025/26, the council
                still budgets 25.7% for reactive maintenance and expects around 1,100 pothole fills. Spend volume
                does not prove every defect was caught within inspection intervals.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Rising Pothole Patch Counts
          </h2>
          <p className="text-surface-600 mb-8">
            Estimated potholes filled — defects requiring a patch to make the road safe, per the council&apos;s
            revenue maintenance reporting
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <caption className="sr-only">Bracknell Forest estimated potholes filled 2021-2026</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Change vs 2021/22</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">740</td>
                  <td className="px-6 py-4 text-center text-surface-500">Baseline</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">755</td>
                  <td className="px-6 py-4 text-center text-amber-600">+2.0%</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">1,255</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">+69.6%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">1,007</td>
                  <td className="px-6 py-4 text-center text-amber-600">+36.1%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">2025/26 (estimated)</td>
                  <td className="px-6 py-4 text-center font-bold">1,100</td>
                  <td className="px-6 py-4 text-center text-amber-600">+48.6%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The 2023/24 spike</h3>
              <p className="text-surface-600 text-sm">
                Pothole fills jumped from 755 to 1,255 in a single year — a 69.6% rise from the 2021/22 baseline.
                That coincides with the period when preventative spend was between 7.6% and 8.4% and U-road RED
                condition hit 23%. The council&apos;s own data links deferred COVID-era residential maintenance to
                that U-road anomaly.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Nearly three patches a day</h3>
              <p className="text-amber-800 text-sm">
                1,007 pothole repairs in 2024/25 works out to roughly 2.8 patches per day across 483.4km. An
                AMBER-rated network still producing that volume of safety-critical defects is one where individual
                potholes routinely form between bi-annual U-road surveys — especially on 70-year-old New Town
                carriageways.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Council admissions */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What Bracknell Forest Acknowledges
          </h2>
          <p className="text-surface-600 mb-8">
            Verbatim admissions from the June 2025 transparency report
          </p>

          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On synchronised ageing of New Town assets</h3>
              <QuoteCard
                quote="All of the 83 subways were built in a relatively short period and as such are all aging together and approaching the end of their design life. This is also true for many of the carriageway, footways and almost every highway asset within the new town area footprint."
                source="Bracknell Forest Council Local Highways Maintenance Transparency Report (10 June 2025)"
              />
            </div>

            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On climate and traffic pressures</h3>
              <QuoteCard
                quote="The increase in severe weather events also places greater pressure on highway assets and this can accelerate deterioration. Furthermore, the aging infrastructure and increasing traffic loads accelerate road wear and tear. These combined circumstances will require more reactive and potentially less cost-effective maintenance."
                source="Bracknell Forest Council Local Highways Maintenance Transparency Report (10 June 2025)"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Planned work and Section 58 */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Planned Work 2025/26 and Section 58
          </h2>
          <p className="text-surface-600 mb-8">
            What the council says it will deliver — and where inspection gaps appear
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-emerald-600">4.8km</div>
              <div className="text-sm text-emerald-800 font-medium mt-1">Carriageway resurfacing (inlay)</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">5.4km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Reinforced micro asphalt</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">5.8km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Surface dressing</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">~1,100</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Potholes expected</div>
            </div>
          </div>

          <div className="bg-surface-50 rounded-xl p-6 border border-surface-200 mb-8">
            <h3 className="font-semibold text-brand-900 mb-3">Coverage maths</h3>
            <p className="text-surface-600 text-sm">
              Planned preventative carriageway treatments total 16km (4.8 + 5.4 + 5.8) against a 483.4km network —
              roughly 3.3% of road length in a single year. The remaining network relies on reactive patching, revenue
              maintenance and bi-annual U-road visual surveys to catch deterioration on infrastructure the council
              describes as approaching end of design life.
            </p>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8 mb-8">
            <h3 className="font-display text-xl font-bold mb-4">Section 41 vs Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under <strong className="text-white">Section 41 of the Highways Act 1980</strong>, Bracknell Forest
              must maintain public highways. To defend a claim under <strong className="text-white">Section 58</strong>,
              it must show a reasonable system for inspecting and repairing the specific defect — not just publish
              an AMBER DfT scorecard with GREEN spend.
            </p>
            <ul className="text-brand-300 space-y-2 text-sm">
              <li>• Was your road on the bi-annual U-road survey cycle — and had its half of the borough been surveyed recently?</li>
              <li>• Did the defect meet intervention criteria during routine safety inspections or revenue maintenance?</li>
              <li>• Were there prior reports (council online form, FixMyStreet) giving actual notice?</li>
              <li>• Does photographic evidence show defect age beyond the survey interval?</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against an AMBER-Rated Borough
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Bracknell Forest invests beyond its DfT allocation — but condition and prevention
            data tell a more mixed story
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — capital spend more than doubles DfT allocation (£6.929m vs £3.293m projected)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Preventative share rising to a projected 30.4% for 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> B/C road condition stable on published SCANNER data
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented HIAMP and HMMP asset-management framework
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-road RED condition improving from 7.2% peak to 6.4% in 2024/25
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a structured Section 58 defence citing spend levels and asset-management plans. Generic
                &quot;council neglect&quot; arguments will struggle without defect-specific evidence.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER overall, condition and best-practice scorecards from DfT
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 323.2km of U-roads — 67% of network — surveyed bi-annually with RED % only
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 0% programmed preventative spend reported in 2020/21; 7.6%–16.5% through 2024
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 1,255 pothole fills in 2023/24 — up 69.6% from 2021/22 baseline
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits New Town assets are &quot;approaching the end of their design life&quot;
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council links COVID-deferred maintenance to U-road deterioration spike
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a borough with GREEN spend and documented asset management, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole — proof of actual notice beyond network surveys</li>
              <li>• Photos showing defect size, depth and age (weathered edges, previous patching)</li>
              <li>• Road class — on a U-road, the bi-annual survey gap and limited RED-only data are structural arguments</li>
              <li>• Timing — if your incident fell during the 2020–2024 reactive-heavy period on ageing infrastructure</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: prior-report search, photo assessment, and citations from Bracknell
              Forest&apos;s own transparency data where it helps you — without pretending the council is failing
              overall.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-12 bg-surface-100 border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-3">
            Report a pothole to Bracknell Forest Council
          </h2>
          <p className="text-surface-600 mb-6">
            Reporting a defect creates a record the council had notice. Do this before claiming — and tell us
            when you reported it so we can reference it in your pack.
          </p>
          <a
            href="https://www.bracknell-forest.gov.uk/roads-parking-and-transport/roads/road-repairs-and-issues/report-potholes-and-other-road-repairs-and-issues"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Report potholes and road repairs — bracknell-forest.gov.uk
            <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Bracknell Forest?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            GREEN spend with AMBER condition demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No bi-annual U-road survey-gap argument</li>
                <li>• No 0% prevention-year context</li>
                <li>• No prior-report search</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 323km U-road bi-annual survey gap documented</li>
                <li>✅ 0% prevention year and 1,255-patch spike cited</li>
                <li>✅ End-of-design-life admissions quoted</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Bracknell Forest</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=bracknell-forest"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Bracknell Forest Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.bracknell-forest.gov.uk/roads-parking-and-transport/roads/roadworks/renewing-road-surfaces/local-highways-maintenance-transparency-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Bracknell Forest Council Local Highways Maintenance Transparency Report (10 June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
